import { useMutation } from "@apollo/client/react";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { SyntheticEvent, useRef, useState } from "react";
import { toast } from "react-toastify";
import UserRoles, { isRole, Role } from "../../../../constants/roles";
import { SaveUserDocument } from "../../../../graphql/types";

import { PencilSimpleIcon } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { PlusCircleIcon } from "@phosphor-icons/react/dist/csr/PlusCircle";
import { Pill } from "../../../../components/ui/Pill";
import { RoleSelection } from "./RoleSelection";

type EditableSelectedRolesProps = {
  roles: Role[];
  globalRoles?: Role[];
  email: string;
  urlName?: string;
  showAddButtonText?: boolean;
  isGlobalSelector?: boolean;
};

const EditableSelectRoles = ({
  roles,
  email,
  urlName,
  showAddButtonText,
  isGlobalSelector,
  globalRoles,
}: EditableSelectedRolesProps) => {
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(roles);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const [saveUser, saveUserState] = useMutation(SaveUserDocument, {
    onCompleted: () => {
      toast.success(`${!urlName ? "Global roles updated" : "Roles updated"}`);
    },
    onError: () => {
      toast.error(`${!urlName ? "Error when updating global roles" : "Error when updating roles"}`);
    },
  });

  const hasSameRoles = selectedRoles.length === roles.length && roles.every((r) => selectedRoles.includes(r));

  const handleOpen = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (saveUserState.loading) return;
    if (selectedRoles.length === 0 || hasSameRoles) return;
    saveUser({
      variables: {
        email,
        urlNames: urlName ? [urlName] : null,
        roles: selectedRoles.map((v) => v),
      },
    });
  };

  const handleCheckboxClick = (e: SyntheticEvent, checked: boolean) => {
    const role = (e.currentTarget as HTMLInputElement).value;
    if (!isRole(role)) return;
    setSelectedRoles((prev) => (checked ? [...prev, role] : prev.filter((r) => r !== role)));
  };

  return (
    <div style={{ position: "relative" }} ref={buttonRef}>
      {selectedRoles.map((val) => (
        <Pill
          key={val}
          size="small"
          role={val}
          label={UserRoles[val].label}
          data-testid={
            isGlobalSelector ? `global-role-chip-${val.toLocaleLowerCase()}` : `role-chip-${val.toLocaleLowerCase()}`
          }
          sx={{ marginLeft: "8px" }}
          variant={isGlobalSelector ? "secondary" : "primary"}
        />
      ))}
      {anchorEl ? null : (
        <IconButton onClick={handleOpen} aria-label={urlName ? "edit roles" : "edit global roles"}>
          {selectedRoles.length === 0 ? (
            <Stack direction="row" sx={{ alignItems: "center" }}>
              <PlusCircleIcon color={theme.palette.text30.main} />
              {showAddButtonText ? (
                <Typography
                  variant="button.large"
                  sx={{
                    color: "text30.main",
                    ml: "4px",
                  }}
                >
                  Add <strong>global</strong> role
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <PencilSimpleIcon color={theme.palette.text30.main} />
          )}
        </IconButton>
      )}
      <RoleSelection
        selectedRoles={selectedRoles}
        globalRoles={globalRoles || []}
        onClose={handleClose}
        anchorEl={anchorEl}
        handleCheckboxClick={handleCheckboxClick}
      />
    </div>
  );
};

export { EditableSelectRoles };
